import { IRegionShort } from 'api/region/types';
import { IResortTopRated } from 'api/home/types';
import {
  IContactInformation,
  IDescriptionsResort,
  IOpeningTimeItem,
  IResortPageInfo,
  IResortPageSnowInfo,
  IResortPathInfo,
  IResortReviews,
  IResortStrengthsOverview,
  IResortTrailmapPage,
  IReviewsGeneral,
  ISnowfallItem,
  OpenFlag,
} from 'api/resort/types';
import { socialLinks } from 'components/blocks/Footer';
import { weekDays } from 'components/resort/LiftTimes';
import { getDefaultDistanceUnit, getLocale } from 'i18n/locale';
import { DistanceType } from 'i18n/types';
import { roundAcHectars, roundCmInch, roundMFeet } from 'i18n/units';
import { ELocale } from 'i18n/localeEnum';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import React from 'react';
import UrlLogoSnow from 'public/assets/white-logo.svg';
import UrlLogoSki from 'public/assets/white-logo_ski.svg';

import { useRouter } from 'next/router';
import { Routes } from 'routes';
import { IMarkupReview } from 'types';
import { formatDateFromStr } from 'util/formatDate';

export type Props = {
  resort: IResortPageInfo,
  averageReviews?: IReviewsGeneral | null,
  reviewList?: IResortReviews | null,
  contact?: IContactInformation | null,
  awards?: IResortStrengthsOverview | null
  schedule?: Array<IOpeningTimeItem>;
};

export type WeatherLdJsonProps = {
  fullResort: IResortPageSnowInfo,
  pathInfo: IResortPathInfo,
};

export type HistoricalSnowfallLdJsonProps = {
  resort: IResortPageInfo,
  pathInfo: IResortPathInfo,
  snowfallInfoAnnual: Array<ISnowfallItem> | null,
  snowfallInfoMonthly: Array<ISnowfallItem> | null,
};

export type ResortSnowReportLdJsonProps = {
  resort: IResortPageSnowInfo | null,
};

export type ResortOverviewLdJsonProps = {
  resort: IResortPageSnowInfo | null,
  pathInfo: IResortPathInfo,
  trailmap: IResortTrailmapPage | null,
  reviewsGeneral: IReviewsGeneral | null,
  resortDescriptionTabs: Array<IDescriptionsResort> | null,
  snowfallInfoAnnual: Array<ISnowfallItem> | null,
};

export type RegionOverviewLdJsonProps = {
  region: IRegionShort,
  resorts: Array<IResortPathInfo>,
  topRatedResorts?: Array<IResortTopRated>,
  numResorts?: number | null,
  description: string,
};

const DEFAULT_WEBSITE_DOMAIN = 'https://www.onthesnow.com';

const SNOW_UNITS = {
  [DistanceType.METRIC]: { unitText: 'centimeters', unitCode: 'CMT' },
  [DistanceType.IMPERIAL]: { unitText: 'inches', unitCode: 'INH' },
} as const;

const RESORT_STATUS_LABELS: Record<OpenFlag, string> = {
  [OpenFlag.Open]: 'Open',
  [OpenFlag.Closed]: 'Closed',
  [OpenFlag.TemporarilyClosed]: 'Temporarily closed',
  [OpenFlag.WeekendsOnly]: 'Weekends only',
  [OpenFlag.NoReportAvailable]: 'No report available',
  [OpenFlag.PermanentlyClosed]: 'Permanently closed',
};

const normalizeDomain = (domain?: string | null) => {
  const value = domain && domain.trim() ? domain : DEFAULT_WEBSITE_DOMAIN;
  return value.replace(/\/$/, '');
};

const resolveAssetUrl = (asset: any, domain: string) => {
  const raw = typeof asset === 'string' ? asset : asset?.src ?? '';
  if (!raw) {
    return domain;
  }
  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }
  if (raw.startsWith('//')) {
    return `https:${raw}`;
  }
  return `${domain}${raw.startsWith('/') ? raw : `/${raw}`}`;
};

export const htmlProps = (data: Object) => ({ dangerouslySetInnerHTML: { __html: JSON.stringify(data) } });

export const normalizeLiftTime = (s: string | null | undefined, fallback: string) => {
  if (!s) {
    return fallback;
  }
  const parts = s.split(':');
  if (parts.length === 3) {
    const [h, m] = parts;
    return `${h}:${m}`;
  }
  return s;
};

export const buildMarkupForResort = (
  {
    resort,
    contact,
    averageReviews,
    reviewList,
    awards,
    schedule: rawSchedule,
  }: Props,
  t: (key: I18nKeyGenerated['common'], params?: any) => string,
) => {
  const route = useRouter();
  const pageURL = process.env.NEXT_PUBLIC_WEBSITE_DOMAIN + route.asPath;
  const reviews: Array<IMarkupReview> = [];
  reviewList?.reviewsList.forEach((review) => {
    const reviewDate = formatDateFromStr(review.date, 'yyyy-MM-dd');
    const name = ((review.firstName || review.firstName) ? `${review.firstName} ${review.lastName}` : '').trim();
    reviews.push({
      '@type': 'Review',
      datePublished: reviewDate,
      reviewBody: review?.review,
      author: {
        '@type': 'Person',
        name: name || 'Anonymous',
      },
    });
  });
  const data: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'SkiResort',
    name: resort.title,
    address: {
      '@type': 'PostalAddress',
      addressCountry: resort.region.country,
      addressRegion: resort.region.state,
      addressLocality: resort.region.title,
    },
    url: pageURL,
    image: resort.image,
  };

  if (contact && data.address) {
    const { address } = data;
    address.streetAddress = contact.address.postOffice;
  }
  if (contact?.email) {
    data.email = `mailto:${contact?.email}`;
  }
  if (contact?.phoneNumber) {
    data.telephone = `${contact?.phoneNumber}`;
  }

  if (reviews) {
    data.review = reviews;
  }
  if (resort?.logo) {
    data.logo = resort?.logo;
  }
  if (resort?.priceRange) {
    data.priceRange = resort?.priceRange;
  }

  if (averageReviews) {
    let total = 0;
    for (let i = 1; i <= 5; i += 1) {
      total += (averageReviews?.totalReviews[i as 1 | 2 | 3 | 4 | 5] ?? 0);
    }

    data.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: averageReviews?.generalRating.overall,
    };

    if (total) {
      data.aggregateRating.ratingCount = total;
      data.aggregateRating.reviewCount = total;
    } else {
      delete data.aggregateRating;
    }
    if (data.aggregateRating && !data.aggregateRating.ratingValue) {
      delete data.aggregateRating;
    }
  }

  if (awards) {
    const categories: { [key: string]: string } = {
      overall: t('resort.overview.overallRegion'),
      allMtnTerrain: t('resort.overview.allMtnTerrainIRegion'),
      familyFriendly: t('resort.overview.familyFriendlyRegion'),
      terrainPark: t('resort.overview.terrainParkRegion'),
      beginner: t('resort.overview.beginnerRegion'),
      apresSki: t('resort.overview.apresSkiRegion'),
      smallSkiArea: t('resort.overview.smallSkiAreaRegion'),
      intermediate: t('resort.overview.intermediateRegion'),
      expert: t('resort.overview.expertRegion'),
    };
    data.award = awards.ratings.map((val) => `${t('resort.overview.positionPrefix', { value: val.position })} ${categories[val.category]} ${val.region.title}`);
  }

  if (rawSchedule) {
    const schedule: Array<IOpeningTimeItem> = (rawSchedule ?? []).filter((a) => !!a.from && !!a.to);
    const hasSchedule = !!schedule.length;
    const days = schedule.filter((d) => !d.isNight).sort((a: IOpeningTimeItem) => a.day);
    const nights = schedule.filter((d) => !!d.isNight).sort((a: IOpeningTimeItem) => a.day);
    const labels = ['Mo', 'Tu', 'Wd', 'Th', 'Fr', 'Sa', 'Su'];
    const displayDays = weekDays.filter((d) => !!schedule.find((tt) => tt.day === d));

    if (hasSchedule) {
      data.openingHours = displayDays.map((d) => {
        const day = days.find((tt) => tt.day === d);
        const night = nights.find((tt) => tt.day === d);
        const parts = [labels[d]];
        if (day) parts.push(`${normalizeLiftTime(day.from, t('common.na'))}-${normalizeLiftTime(day.to, t('common.na'))}`);
        if (night) parts.push(`${normalizeLiftTime(night.from, t('common.na'))}-${normalizeLiftTime(night.to, t('common.na'))}`);
        if (day || night) {
          return parts.join(' ');
        }
        return null;
      }).filter((d) => !!d);
    }
  }

  if (contact) {
    data.geo = {
      '@type': 'GeoCoordinates',
      latitude: contact.address.lat,
      longitude: contact.address.lng,
    };
  }
  return htmlProps(data);
};

export const buildMarkupForWeather = ({ fullResort, pathInfo }: WeatherLdJsonProps) => {
  const domain = normalizeDomain(process.env.NEXT_PUBLIC_WEBSITE_DOMAIN);
  const pagePath = Routes.resortWeather(pathInfo);
  const pageUrl = `${domain}${pagePath}`;
  const organizationId = `${domain}/#organization`;

  const { region } = fullResort;
  const address: Record<string, unknown> = {
    '@type': 'PostalAddress',
  };
  let hasAddressDetails = false;
  if (region?.title) {
    address.addressLocality = region.title;
    hasAddressDetails = true;
  }
  if (region?.state) {
    address.addressRegion = region.state;
    hasAddressDetails = true;
  }
  if (region?.country_code) {
    address.addressCountry = region.country_code;
    hasAddressDetails = true;
  } else if (region?.country) {
    address.addressCountry = region.country;
    hasAddressDetails = true;
  }

  if (fullResort.zip) {
    address.postalCode = fullResort.zip;
    hasAddressDetails = true;
  }

  const place: Record<string, unknown> = {
    '@type': 'SkiResort',
    name: fullResort.title,
  };

  if (hasAddressDetails) {
    place.address = address;
  }

  if (typeof fullResort.latitude === 'number' && typeof fullResort.longitude === 'number') {
    place.geo = {
      '@type': 'GeoCoordinates',
      latitude: fullResort.latitude,
      longitude: fullResort.longitude,
    };
  }

  const locale = getLocale() as ELocale;
  const showUSLogo = [ELocale.EN_US, ELocale.EN_GB, ELocale.CS_CZ, ELocale.SK_SK].includes(locale);
  const logoUrl = resolveAssetUrl(showUSLogo ? UrlLogoSnow : UrlLogoSki, domain);
  const sameAs = [
    socialLinks.facebook,
    socialLinks.instagram,
    socialLinks.twitter,
    socialLinks.youtube,
  ].filter(Boolean);

  const headline = `${fullResort.title} Weather Forecast`;
  const locationParts = [region?.title, region?.state, region?.country].filter((part) => !!part);
  const description = `Current weather and snow forecast for ${fullResort.title}${locationParts.length ? `, ${locationParts.join(', ')}` : ''}.`;
  const inLanguage = locale;
  const ONTHESNOW_LOCALES = [ELocale.EN_US, ELocale.EN_GB, ELocale.CS_CZ, ELocale.SK_SK];
  const organizationName = ONTHESNOW_LOCALES.includes(locale as ELocale) ? 'OnTheSnow' : 'Skiinfo';

  const mainEntityOfPage = {
    '@type': 'WebPage',
    '@id': pageUrl,
    url: pageUrl,
    name: headline,
    description,
    inLanguage,
    about: place,
    isPartOf: {
      '@type': 'WebSite',
      url: domain,
    },
    publisher: {
      '@type': 'Organization',
      '@id': organizationId,
    },
  };

  const organizationData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': organizationId,
    name: organizationName,
    url: domain,
    sameAs,
    logo: {
      '@type': 'ImageObject',
      url: logoUrl,
    },
  };

  const websiteData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: domain,
    publisher: {
      '@type': 'Organization',
      '@id': organizationId,
      name: organizationName,
      url: domain,
      logo: {
        '@type': 'ImageObject',
        url: logoUrl,
        width: 200,
        height: 60,
      },
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${domain}?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
    mainEntityOfPage,
    headline,
    description,
  };

  const webPageData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': pageUrl,
    url: pageUrl,
    name: headline,
    description,
    inLanguage,
    about: place,
    isPartOf: {
      '@type': 'WebSite',
      url: domain,
    },
    publisher: {
      '@type': 'Organization',
      '@id': organizationId,
    },
  };

  return [organizationData, websiteData, webPageData];
};

export const LdJsonWeather = (props: WeatherLdJsonProps) => {
  const markups = buildMarkupForWeather(props);

  return (
    <>
      {markups.map((markup) => {
        const markupType = (markup as Record<string, unknown>)['@type'];
        const key = typeof markupType === 'string' ? `ldjson-weather-${markupType}` : 'ldjson-weather';

        return (
          <script
            key={key}
            type="application/ld+json"
            {...htmlProps(markup)}
          />
        );
      })}
      {markups.length === 0 && (
        <script
          key="ldjson-weather-empty"
          type="application/ld+json"
          {...htmlProps([])}
        />
      )}
    </>
  );
};

export const buildMarkupForResortOverview = ({
  resort,
  pathInfo,
  trailmap,
  reviewsGeneral,
  resortDescriptionTabs,
  snowfallInfoAnnual,
}: ResortOverviewLdJsonProps) => {
  if (!resort) {
    return null;
  }

  const domain = normalizeDomain(process.env.NEXT_PUBLIC_WEBSITE_DOMAIN);
  const pageUrl = `${domain}${Routes.resortTripPlanningOverview(pathInfo)}`;

  const data: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'SkiResort',
    name: resort.title,
    url: pageUrl,
  };

  const resolveFirstParagraph = (html?: string) => {
    if (!html) {
      return undefined;
    }
    const firstParagraphMatch = html.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
    const raw = firstParagraphMatch ? firstParagraphMatch[1] : html;
    const text = raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    return text || undefined;
  };

  const overviewTab = (resortDescriptionTabs ?? []).find((tab) => tab.tab === 'resort_profile');
  const description = resolveFirstParagraph(overviewTab?.description);
  if (description) {
    data.description = description;
  }

  const { region } = resort;
  if (region) {
    const address: Record<string, string> = {};
    if (region.title) {
      address.addressLocality = region.title;
    }
    if (region.state) {
      address.addressRegion = region.state;
    }
    if (region.country_code || region.country) {
      address.addressCountry = region.country_code || region.country;
    }
    if (Object.keys(address).length) {
      data.address = {
        '@type': 'PostalAddress',
        ...address,
      };
    }
  }

  if (typeof resort.latitude === 'number' && typeof resort.longitude === 'number') {
    data.geo = {
      '@type': 'GeoCoordinates',
      latitude: resort.latitude,
      longitude: resort.longitude,
    };
  }

  const ratingValue = reviewsGeneral?.generalRating?.overall;
  if (typeof ratingValue === 'number' && !Number.isNaN(ratingValue)) {
    const totalReviews = reviewsGeneral?.totalReviews
      ? Object.values(reviewsGeneral.totalReviews).reduce((acc, val) => acc + (val ?? 0), 0)
      : 0;

    const aggregateRating: Record<string, any> = {
      '@type': 'AggregateRating',
      ratingValue,
    };

    if (totalReviews) {
      aggregateRating.reviewCount = totalReviews;
    }

    data.aggregateRating = aggregateRating;
  }

  const additionalProperty: Array<Record<string, any>> = [];

  const pushElevationProperty = (name: string, value?: number | null) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return;
    }
    const converted = roundMFeet(value, DistanceType.IMPERIAL);
    if (typeof converted === 'number' && !Number.isNaN(converted)) {
      additionalProperty.push({
        '@type': 'PropertyValue',
        name,
        value: converted,
        unitText: 'ft',
      });
    }
  };

  pushElevationProperty('Summit elevation', trailmap?.elevation?.summit ?? undefined);
  pushElevationProperty('Base elevation', trailmap?.elevation?.base ?? undefined);
  pushElevationProperty('Vertical drop', trailmap?.elevation?.verticalDrop ?? undefined);

  if (trailmap?.runs && typeof trailmap.runs.skiableAc === 'number' && !Number.isNaN(trailmap.runs.skiableAc)) {
    const converted = roundAcHectars(trailmap.runs.skiableAc, DistanceType.IMPERIAL);
    if (typeof converted === 'number' && !Number.isNaN(converted)) {
      additionalProperty.push({
        '@type': 'PropertyValue',
        name: 'Skiable terrain',
        value: converted,
        unitText: 'acres',
      });
    }
  }

  if (typeof trailmap?.runs?.total === 'number' && trailmap.runs.total > 0) {
    additionalProperty.push({
      '@type': 'PropertyValue',
      name: 'Total trails',
      value: trailmap.runs.total,
    });
  }

  if (trailmap?.lifts) {
    const totalLifts = Object.values(trailmap.lifts).reduce((acc, val) => {
      if (typeof val === 'number' && !Number.isNaN(val)) {
        return acc + val;
      }
      return acc;
    }, 0);
    if (totalLifts > 0) {
      additionalProperty.push({
        '@type': 'PropertyValue',
        name: 'Total lifts',
        value: totalLifts,
      });
    }
  }

  if (snowfallInfoAnnual?.length) {
    const totalSnow = snowfallInfoAnnual.reduce((acc, item) => acc + (item.totalSnow ?? 0), 0);
    if (totalSnow > 0) {
      const averageSnow = totalSnow / snowfallInfoAnnual.length;
      const convertedSnow = roundCmInch(averageSnow, DistanceType.IMPERIAL);
      if (typeof convertedSnow === 'number' && convertedSnow > 0) {
        additionalProperty.push({
          '@type': 'PropertyValue',
          name: 'Annual average snowfall',
          value: convertedSnow,
          unitText: 'inches',
        });
      }
    }
  }

  if (additionalProperty.length) {
    data.additionalProperty = additionalProperty;
  }

  return data;
};

export const LdJsonResortOverview = (props: ResortOverviewLdJsonProps) => {
  const markup = buildMarkupForResortOverview(props);

  if (!markup) {
    return null;
  }

  return (
    <script
      type="application/ld+json"
      {...htmlProps(markup)}
    />
  );
};

export const buildMarkupForRegionOverview = ({
  region,
  resorts,
  topRatedResorts,
  numResorts,
  description,
}: RegionOverviewLdJsonProps) => {
  if (!region) {
    return null;
  }

  const domain = normalizeDomain(process.env.NEXT_PUBLIC_WEBSITE_DOMAIN);
  const ensureAbsoluteUrl = (value: string) => (/^https?:\/\//i.test(value) ? value : `${domain}${value}`);

  const trimmedRegionTitle = typeof region.title === 'string' ? region.title.trim() : '';
  const resolvedName = trimmedRegionTitle ? `${trimmedRegionTitle} Ski Resorts` : undefined;

  const data: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'TouristDestination',
  };

  if (resolvedName) {
    data.name = resolvedName;
  }

  if (description) {
    data.description = description;
  }

  const containedInPlace = (() => {
    if (region.state && region.country) {
      return {
        '@type': 'Country',
        name: region.country,
      };
    }
    if (region.country) {
      return {
        '@type': 'Country',
        name: region.country,
      };
    }
    return undefined;
  })();

  if (containedInPlace) {
    data.containedInPlace = containedInPlace;
  }

  const uniqueResorts = new Map<string, IResortPathInfo>();
  (resorts ?? []).forEach((resort) => {
    if (resort?.uuid && !uniqueResorts.has(resort.uuid)) {
      uniqueResorts.set(resort.uuid, resort);
    }
  });
  topRatedResorts?.forEach((item) => {
    const resortPath = item?.resortPath;
    if (resortPath?.uuid && !uniqueResorts.has(resortPath.uuid)) {
      uniqueResorts.set(resortPath.uuid, resortPath);
    }
  });

  const resolvedResortCount = (() => {
    if (typeof numResorts === 'number' && numResorts > 0) {
      return numResorts;
    }
    return uniqueResorts.size;
  })();

  if (resolvedResortCount > 0) {
    data.additionalProperty = {
      '@type': 'PropertyValue',
      name: 'Number of ski resorts',
      value: resolvedResortCount,
    };
  }

  const ratingMap = new Map<string, { ratingValue: string, reviewCount?: number }>();
  topRatedResorts?.forEach((item) => {
    const uuid = item?.resortPath?.uuid ?? item?.resort?.uuid;
    const ratingRaw = item?.resort?.rating;
    const ratingValue = typeof ratingRaw === 'number' ? ratingRaw.toFixed(1) : undefined;
    const reviewCount = item?.resort?.reviewsCount;
    const ratingNumeric = typeof ratingValue === 'string' ? Number(ratingValue) : NaN;
    if (!uuid || typeof ratingValue !== 'string' || Number.isNaN(ratingNumeric) || ratingNumeric <= 0) {
      return;
    }
    ratingMap.set(uuid, {
      ratingValue,
      reviewCount: typeof reviewCount === 'number' && reviewCount > 0 ? reviewCount : undefined,
    });
  });

  const containsPlace = Array.from(uniqueResorts.values())
    .slice(0, 50)
    .map((resort) => {
      const resortPath = Routes.resortTripPlanningOverview(resort);
      const resortUrl = ensureAbsoluteUrl(resortPath);
      const nameValue = resort.title || resort.title_original || resort.slug;
      if (!nameValue) {
        return null;
      }
      const place: Record<string, any> = {
        '@type': 'SkiResort',
        name: nameValue,
        url: resortUrl,
      };
      const aggregate = ratingMap.get(resort.uuid);
      if (aggregate) {
        const aggregateRating: Record<string, any> = {
          '@type': 'AggregateRating',
          ratingValue: aggregate.ratingValue,
        };
        if (typeof aggregate.reviewCount === 'number') {
          aggregateRating.reviewCount = aggregate.reviewCount;
        }
        place.aggregateRating = aggregateRating;
      }
      return place;
    })
    .filter((item): item is Record<string, any> => !!item);

  if (containsPlace.length) {
    data.containsPlace = containsPlace;
  }

  return data;
};

export const LdJsonRegionOverview = (props: RegionOverviewLdJsonProps) => {
  const {
    region,
    description,
    ...rest
  } = props;
  const { t: seoT } = useOTSTranslation('seo');

  if (!region) {
    return null;
  }

  const resolvedDescription = description || seoT('MetaRegion.profile.description', { region: region.title });

  const markup = buildMarkupForRegionOverview({
    ...rest,
    region,
    description: resolvedDescription,
  });

  if (!markup) {
    return null;
  }

  return (
    <script
      type="application/ld+json"
      {...htmlProps(markup)}
    />
  );
};

export const buildMarkupForResortSnowReport = ({ resort }: ResortSnowReportLdJsonProps) => {
  if (!resort) {
    return null;
  }

  const formatISODate = (value?: string | null) => {
    if (!value) {
      return null;
    }
    const formatted = formatDateFromStr(value, 'yyyy-MM-dd');
    return formatted || null;
  };

  const parseDateSafe = (value?: string | null) => {
    if (!value) {
      return null;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const distanceUnit = getDefaultDistanceUnit();
  const snowUnit = SNOW_UNITS[distanceUnit];

  const convertSnowValue = (value?: number | null) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return undefined;
    }
    const converted = roundCmInch(value, distanceUnit);
    return typeof converted === 'number' ? converted : undefined;
  };

  const sortedRecent = [...(resort.recent ?? [])].sort((a, b) => {
    const dateA = parseDateSafe(a?.date);
    const dateB = parseDateSafe(b?.date);
    if (!dateA && !dateB) {
      return 0;
    }
    if (!dateA) {
      return 1;
    }
    if (!dateB) {
      return -1;
    }
    return dateB.getTime() - dateA.getTime();
  });

  const lastSnowfallRecord = sortedRecent.find((item) => typeof item?.snow === 'number');

  let lastSnowfallAmount = convertSnowValue(lastSnowfallRecord?.snow ?? null);
  if (typeof lastSnowfallAmount === 'undefined') {
    lastSnowfallAmount = convertSnowValue(resort.snow?.last24 ?? null);
  }

  let lastSnowfallDate = lastSnowfallRecord ? formatISODate(lastSnowfallRecord.date) : null;
  if (!lastSnowfallDate && typeof lastSnowfallAmount !== 'undefined') {
    lastSnowfallDate = formatISODate(resort.updatedAt);
  }

  const baseDepth = convertSnowValue(resort.snow?.base ?? null);
  const summitDepth = convertSnowValue(resort.snow?.summit ?? null);

  const openTrails = typeof resort.runs?.open === 'number' ? resort.runs.open : null;
  const openLifts = typeof resort.lifts?.open === 'number' ? resort.lifts.open : null;

  const lastReportUpdate = formatISODate(resort.updatedAt);
  const projectedOpening = formatISODate(resort.status?.openingDate);
  const projectedClosing = formatISODate(resort.status?.closingDate);

  const statusValue = resort.status?.openFlag !== undefined
    ? (RESORT_STATUS_LABELS[resort.status.openFlag] ?? 'Unknown')
    : 'Unknown';

  const address: Record<string, string> = {};
  const { region } = resort;

  if (region?.title) {
    address.addressLocality = region.title;
  }
  if (region?.state) {
    address.addressRegion = region.state;
  }
  if (region?.country_code || region?.country) {
    address.addressCountry = region.country_code || region.country;
  }

  const additionalPropertyBase = [
    {
      '@type': 'PropertyValue',
      name: 'Resort status',
      value: statusValue,
    },
    {
      '@type': 'PropertyValue',
      name: 'Last snow report update',
      value: lastReportUpdate ?? null,
    },
    {
      '@type': 'PropertyValue',
      name: 'Base snow depth',
      value: typeof baseDepth === 'number' ? baseDepth : null,
      unitText: snowUnit.unitText,
    },
    {
      '@type': 'PropertyValue',
      name: 'Summit snow depth',
      value: typeof summitDepth === 'number' ? summitDepth : null,
      unitText: snowUnit.unitText,
    },
    {
      '@type': 'PropertyValue',
      name: 'Last snowfall amount',
      value: typeof lastSnowfallAmount === 'number' ? lastSnowfallAmount : null,
      unitText: snowUnit.unitText,
    },
    {
      '@type': 'PropertyValue',
      name: 'Last snowfall date',
      value: lastSnowfallDate ?? null,
    },
    {
      '@type': 'PropertyValue',
      name: 'Open trails',
      value: openTrails,
    },
    {
      '@type': 'PropertyValue',
      name: 'Open lifts',
      value: openLifts,
    },
    {
      '@type': 'PropertyValue',
      name: 'Projected season opening date',
      value: projectedOpening ?? null,
    },
    {
      '@type': 'PropertyValue',
      name: 'Projected season closing date',
      value: projectedClosing ?? null,
    },
  ];

  const additionalProperty = additionalPropertyBase.filter((property) => property.value !== null && typeof property.value !== 'undefined');

  const data: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'SkiResort',
    name: resort.title,
    additionalProperty,
  };

  if (Object.keys(address).length) {
    data.address = {
      '@type': 'PostalAddress',
      ...address,
    };
  }

  return data;
};

export const LdJsonResortSnowReport = (props: ResortSnowReportLdJsonProps) => {
  const markup = buildMarkupForResortSnowReport(props);

  if (!markup) {
    return null;
  }

  return (
    <script
      type="application/ld+json"
      {...htmlProps(markup)}
    />
  );
};

export const buildMarkupForHistoricalSnowfall = ({
  resort,
  pathInfo,
  snowfallInfoAnnual,
  snowfallInfoMonthly,
}: HistoricalSnowfallLdJsonProps) => {
  const annualItems = snowfallInfoAnnual ?? [];
  if (!annualItems.length || !resort) {
    return null;
  }

  const HISTORICAL_SNOWFALL_LICENSE_URL = 'https://creativecommons.org/licenses/by/4.0/';
  const HISTORICAL_MEASUREMENT_TECHNIQUE = 'Aggregated historical snowfall statistics derived from resort snow reports.';

  const toISODate = (date: Date) => date.toISOString().split('T')[0];

  const resolveTemporalCoverage = () => {
    const parseDateSafe = (value: string) => {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const dates = [
      ...(snowfallInfoMonthly ?? []).map((item) => parseDateSafe(item.date)),
      ...(snowfallInfoAnnual ?? []).map((item) => parseDateSafe(item.date)),
    ].filter((date): date is Date => !!date);

    if (!dates.length) {
      return undefined;
    }

    dates.sort((a, b) => a.getTime() - b.getTime());
    const start = dates[0];
    const end = dates[dates.length - 1];

    return `${toISODate(start)}/${toISODate(end)}`;
  };

  const getAverageSnowfallValue = (
    items: Array<ISnowfallItem> | null,
    key: keyof Pick<ISnowfallItem, 'totalSnow' | 'snowDays' | 'baseDepth' | 'maxBaseDepth' | 'biggestSnowfall'>,
  ) => {
    if (!items?.length) {
      return undefined;
    }

    const total = items.reduce((acc, item) => acc + (item[key] ?? 0), 0);
    return Math.ceil(total / items.length);
  };

  const distanceUnit = getDefaultDistanceUnit();
  const snowUnit = SNOW_UNITS[distanceUnit];

  const convertSnowValue = (value: number | undefined) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return undefined;
    }

    const converted = roundCmInch(value, distanceUnit);
    return typeof converted === 'number' ? converted : undefined;
  };

  const buildSpatialCoverage = () => {
    const { region } = resort;
    if (!region) {
      return undefined;
    }

    const nameParts = [region.title, region.state, region.country].filter(Boolean);
    const place: Record<string, any> = {
      '@type': 'Place',
      name: nameParts.length ? nameParts.join(', ') : resort.title,
    };

    const address: Record<string, string> = {};
    if (region.title) {
      address.addressLocality = region.title;
    }
    if (region.state) {
      address.addressRegion = region.state;
    }
    if (region.country_code || region.country) {
      address.addressCountry = region.country_code || region.country;
    }

    if (Object.keys(address).length) {
      place.address = {
        '@type': 'PostalAddress',
        ...address,
      };
    }

    return place;
  };

  const domain = normalizeDomain(process.env.NEXT_PUBLIC_WEBSITE_DOMAIN);
  const pagePath = Routes.resortSnowfall(pathInfo);
  const pageUrl = `${domain}${pagePath}`;
  const datasetId = `${pageUrl}#dataset`;

  const locale = getLocale() as ELocale;
  const ONTHESNOW_LOCALES = [ELocale.EN_US, ELocale.EN_GB, ELocale.CS_CZ, ELocale.SK_SK];
  const organizationName = ONTHESNOW_LOCALES.includes(locale) ? 'OnTheSnow' : 'Skiinfo';

  const averageAnnualSnowfall = convertSnowValue(getAverageSnowfallValue(annualItems, 'totalSnow'));
  const averageSnowfallDays = getAverageSnowfallValue(annualItems, 'snowDays');
  const averageBaseDepth = convertSnowValue(getAverageSnowfallValue(annualItems, 'baseDepth'));
  const averageMaxBaseDepth = convertSnowValue(getAverageSnowfallValue(annualItems, 'maxBaseDepth'));
  const biggestSnowfallRaw = annualItems.reduce(
    (max, item) => (item.biggestSnowfall > max ? item.biggestSnowfall : max),
    0,
  );
  const biggestSnowfall = convertSnowValue(biggestSnowfallRaw);

  const variableMeasured: Array<Record<string, any>> = [];

  if (typeof averageAnnualSnowfall !== 'undefined') {
    variableMeasured.push({
      '@type': 'PropertyValue',
      name: 'Average annual snowfall',
      value: averageAnnualSnowfall,
      unitText: snowUnit.unitText,
      unitCode: snowUnit.unitCode,
    });
  }

  if (typeof averageSnowfallDays !== 'undefined') {
    variableMeasured.push({
      '@type': 'PropertyValue',
      name: 'Average snowfall days per season',
      value: averageSnowfallDays,
      unitText: 'days',
      unitCode: 'DAY',
    });
  }

  if (typeof averageBaseDepth !== 'undefined') {
    variableMeasured.push({
      '@type': 'PropertyValue',
      name: 'Average base depth',
      value: averageBaseDepth,
      unitText: snowUnit.unitText,
      unitCode: snowUnit.unitCode,
    });
  }

  if (typeof averageMaxBaseDepth !== 'undefined') {
    variableMeasured.push({
      '@type': 'PropertyValue',
      name: 'Average max base depth',
      value: averageMaxBaseDepth,
      unitText: snowUnit.unitText,
      unitCode: snowUnit.unitCode,
    });
  }

  if (typeof biggestSnowfall !== 'undefined') {
    variableMeasured.push({
      '@type': 'PropertyValue',
      name: 'Biggest single snowfall',
      value: biggestSnowfall,
      unitText: snowUnit.unitText,
      unitCode: snowUnit.unitCode,
    });
  }

  // eslint-disable-next-line max-len
  const description = `Historical snowfall statistics for ${resort.title}, including average annual snowfall, snowfall days, and base depth over the past seasons.`;

  const data: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    '@id': datasetId,
    name: `${resort.title} Historical Snowfall`,
    description,
    url: pageUrl,
    variableMeasured,
    creator: {
      '@type': 'Organization',
      name: organizationName,
      url: domain,
    },
    publisher: {
      '@type': 'Organization',
      name: organizationName,
      url: domain,
    },
    license: HISTORICAL_SNOWFALL_LICENSE_URL,
    measurementTechnique: HISTORICAL_MEASUREMENT_TECHNIQUE,
  };

  const temporalCoverage = resolveTemporalCoverage();
  if (temporalCoverage) {
    data.temporalCoverage = temporalCoverage;
  }

  const spatialCoverage = buildSpatialCoverage();
  if (spatialCoverage) {
    data.spatialCoverage = spatialCoverage;
  }

  return data;
};

export const LdJsonHistoricalSnowfall = (props: HistoricalSnowfallLdJsonProps) => {
  const markup = buildMarkupForHistoricalSnowfall(props);

  if (!markup) {
    return null;
  }

  return (
    <script
      type="application/ld+json"
      {...htmlProps(markup)}
    />
  );
};

export const LdJsonSkiResort = (props: Props) => {
  const { t } = useOTSTranslation('common');
  const data = buildMarkupForResort(props, t);

  return <script type="application/ld+json" {...data} />;
};

export const LdJsonBreadCrumbsResort = ({
  resort,
  region,
  fn,
}: { resort: IResortPathInfo, region: IRegionShort, fn: (r: IResortPathInfo) => string }) => {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement:
      [
        {
          '@type': 'ListItem',
          position: 1,
          item:
            {
              '@id': Routes.regionSnowReport(region),
              name: region.title,
            },
        },
        {
          '@type': 'ListItem',
          position: 2,
          item:
            {
              '@id': fn(resort),
              name: resort.title,
            },
        },
      ],
  };
  const props = htmlProps(data);

  return <script type="application/ld+json" {...props} />;
};

export const LdJsonSearchAction = () => {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: `${process.env.NEXT_PUBLIC_WEBSITE_DOMAIN}`,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${process.env.NEXT_PUBLIC_WEBSITE_DOMAIN}?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const props = htmlProps(data);

  return <script type="application/ld+json" {...props} />;
};

export const LdJsonOrganization = () => {
  const { t } = useOTSTranslation('common');
  const showUSLogo = [ELocale.EN_US, ELocale.EN_GB, ELocale.CS_CZ, ELocale.SK_SK].includes(getLocale() as ELocale);

  const data = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    url: process.env.NEXT_PUBLIC_WEBSITE_DOMAIN,
    name: t('homePage.companyName'),
    description: t('homePage.description'),
    foundingDate: 1968,
    logo: showUSLogo ? UrlLogoSnow : UrlLogoSki,
    legalName: t('homePage.legalName'),
    numberOfEmployees: t('homePage.employees'),
    sameAs: [socialLinks.twitter, socialLinks.facebook, socialLinks.instagram],
  };

  const props = htmlProps(data);

  return <script type="application/ld+json" {...props} />;
};
